import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, Trophy, RotateCcw, FileText } from "lucide-react";
import { API } from "../App";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";

export const QuizModal = ({ contentId, onClose, onPassed, onOpenLeadSheet }) => {
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);

  const load = () => {
    setQuiz(null); setAnswers({}); setResult(null); setError(null);
    if (boxRef.current) boxRef.current.scrollTop = 0;
    axios.get(`${API}/quizzes/${contentId}`).then((r) => setQuiz(r.data)).catch((e) => setError(e.response?.data?.detail || "Could not load the quiz"));
  };

  useEffect(() => {
    if (contentId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentId]);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/quizzes/${contentId}/submit`, { answers });
      setResult(r.data);
      setTimeout(() => boxRef.current?.querySelector('[data-testid="quiz-result"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
      if (r.data.passed) onPassed?.(contentId, r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not submit");
    } finally {
      setBusy(false);
    }
  };

  const byId = Object.fromEntries((result?.results || []).map((r) => [r.id, r]));
  const allAnswered = quiz && quiz.questions.every((q) => answers[q.id]);

  return (
    <Dialog open={!!contentId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent ref={boxRef} className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="quiz-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">{quiz ? `Quiz: ${quiz.title}` : "Song Quiz"}</DialogTitle>
          <DialogDescription>
            {quiz ? `${quiz.course_name} · Get ${quiz.pass_score} of ${quiz.questions.length} right to pass${quiz.has_lead_sheet ? " and unlock the Lead Sheet" : ""}.` : " "}
          </DialogDescription>
        </DialogHeader>

        {error && <p className="py-6 text-center text-sm text-muted-foreground" data-testid="quiz-error">{error}</p>}
        {!quiz && !error && <Loader2 className="mx-auto my-8 h-6 w-6 animate-spin text-primary" />}

        {quiz && (
          <ol className="space-y-6">
            {quiz.questions.map((q, i) => {
              const r = byId[q.id];
              return (
                <li key={q.id} data-testid={`quiz-question-${i}`}>
                  <p className="mb-2 flex gap-2 font-medium">
                    <span className="text-primary">{i + 1}.</span>
                    <span className="flex-1">{q.question}</span>
                    {r && (r.correct ? <CheckCircle2 className="h-5 w-5 shrink-0 text-green-600" aria-label="Correct" /> : <XCircle className="h-5 w-5 shrink-0 text-red-500" aria-label="Incorrect" />)}
                  </p>
                  <div className="space-y-2" role="radiogroup" aria-label={`Question ${i + 1}`}>
                    {q.choices.map((c) => {
                      const picked = answers[q.id] === c.id;
                      const isRight = r?.correct_choice_id === c.id;
                      return (
                        <button
                          key={c.id}
                          type="button"
                          role="radio"
                          aria-checked={picked}
                          disabled={!!result}
                          onClick={() => setAnswers((a) => ({ ...a, [q.id]: c.id }))}
                          className={`w-full rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ${isRight ? "border-green-500 bg-green-50" : picked ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"}`}
                          data-testid={`quiz-choice-${i}-${c.id}`}
                        >
                          {c.text}
                        </button>
                      );
                    })}
                  </div>
                  {r?.explanation && <p className="mt-2 text-xs text-muted-foreground">{r.explanation}</p>}
                </li>
              );
            })}
          </ol>
        )}

        {result && (
          <div className={`rounded-xl border p-4 ${result.passed ? "border-green-300 bg-green-50" : "border-amber-300 bg-amber-50"}`} data-testid="quiz-result">
            <div className="flex items-center gap-3">
              {result.passed ? <Trophy className="h-7 w-7 text-green-600" /> : <RotateCcw className="h-7 w-7 text-amber-600" />}
              <div>
                <p className="font-semibold" data-testid="quiz-score">{result.score} of {result.total} correct · {result.passed ? "Passed!" : "Not quite"}</p>
                <p className="text-sm text-muted-foreground">
                  {result.passed
                    ? result.lead_sheet_unlocked ? "Your Lead Sheet is unlocked and saved in My Lead Sheetz." : "Nice work. You know this rule."
                    : `You need ${result.pass_score} to pass. Questions you missed are marked below. Try again anytime.`}
                </p>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {result.passed && result.lead_sheet_unlocked && (
                <Button size="sm" onClick={() => { onClose(); onOpenLeadSheet?.(contentId); }} data-testid="quiz-open-lead-sheet-btn"><FileText className="mr-1.5 h-4 w-4" /> Open Lead Sheet</Button>
              )}
              {!result.passed && <Button size="sm" onClick={load} data-testid="quiz-retry-btn"><RotateCcw className="mr-1.5 h-4 w-4" /> Try again</Button>}
              {result.passed && <Button size="sm" variant="outline" onClick={onClose} data-testid="quiz-done-btn">Done</Button>}
            </div>
          </div>
        )}

        {quiz && !result && (
          <Button className="w-full" disabled={!allAnswered || busy} onClick={submit} data-testid="quiz-submit-btn">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {allAnswered ? "Submit answers" : `Answer all ${quiz.questions.length} questions`}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};
